// The Masterminds section's rules (docs/mastermind-admin-plan.md 3.2 to 3.4, and the critique's build plan of 20 Oct,
// docs/accelerator-ux-build-plan.md P3), from hub.mastermind_members' rows and hub.accountability_log's staff version
// to what the members table, the member view and the person page's card say. Pure: it imports only the words, the
// levels, the log's counts and the date helpers, so tests/e2e/admin-masterminds.spec.js checks it on its own.
import { isBehind, levelFromCounts, countsFromSummary } from '../../products/mastermind/levels.js'
import { MM, day } from './words.js'
import { accountability, dm, dmy, wdm } from '../../products/mastermind/log.js'
import { uk } from '../../lib/format.js'

const DAY = 86400000

// The 4 numbers, as private.mm_rules reads them: missing or bad numbers take the defaults. stored: the mastermind's
// rules from hub.accountability_rules since migration 81 (data.js loadRules; {} when it has none), or null on a
// database before 81, where they're still in the product's settings. keptSince: the day the hub started keeping the
// record, before which a goal's days aren't judged.
export function rulesFrom(product, stored = null) {
  const g = (stored && typeof stored === 'object' ? stored : null) || product?.settings?.guarantee || {}
  const num = (v, lo, hi, d) => (Number.isInteger(Number(v)) && String(v).trim() !== '' && Number(v) >= lo && Number(v) <= hi ? Number(v) : d)
  return {
    limit: num(g.absence_limit, 0, 12, 2),
    pct: num(g.actions_pct, 1, 100, 80),
    days: num(g.goal_days, 1, 366, 30),
    catchUp: num(g.catch_up_days, 1, 366, 7),
    months: product?.term_months || 12,
    keptSince: typeof g.kept_since === 'string' ? g.kept_since : null,
  }
}

// Everything that needs Adam, from the summary's flags, in the order he does them: a review, the register marks, the
// goal, a month's actions. [{ kind, text, also, month, session }] (the critique's admin 4: every need, not just the first)
export function needsAll(sum) {
  const f = sum?.flags || {}
  const out = []
  if (f.review_due) out.push({ kind: 'review', text: MM.needsReview(f.review_due.month), also: MM.alsoWords.review(f.review_due.month), month: f.review_due.month, session: f.review_due.session_id, at: f.review_due.starts_at })
  const register = f.register_missing || []
  if (register.length) out.push({ kind: 'register', text: MM.needsRegister, also: MM.alsoWords.register(register.length), session: register[0].session_id })
  if (f.goal_not_agreed) out.push({ kind: 'goal', text: MM.needsGoal, also: MM.alsoWords.goal })
  if (f.no_actions) out.push({ kind: 'actions', text: MM.needsActions(f.no_actions.next_month), also: MM.alsoWords.actions(f.no_actions.next_month), month: f.no_actions.next_month })
  return out
}

// Where they stand on their accountability, in words: "On track", "Behind: 2 sessions missed, 3 actions not done",
// "Getting started" (src/products/mastermind/levels.js). The guarantee keeps its own rules, in Guarantee rules and the
// guarantee check, apart from this.
export function standWords(sum) {
  return MM.levelLine(levelFromCounts(sum))
}

// Worst first: Well behind, Behind, A little behind, then on track and getting started
const RANK = { well_behind: 0, behind: 1, a_little_behind: 2, on_track: 3, getting_started: 3 }
const MONEY = ['overdue', 'suspended', 'paused']

// Who set a level by hand, as the head, the list and the person card name them: 'you' (me: the staff member's own
// person), else their first name, else another admin
export function whoSet(set, me = null) {
  if (!set) return null
  if (me && set.set_by === me) return 'you'
  return set.set_by_name || MM.anotherAdmin
}

// 1 member's row in the table (and on a phone card). Until the summary holds the log's own counts (summary.log, the
// build plan's migration 80) its actions are counted once per action however often it was carried, where the member's
// page counts each month's own (Tom: 3 here, 6 there), so the row says no number of actions the member's page could
// contradict: no Actions share, and where they stand by its word and the sessions alone. A level Adam set by hand
// (summary.level_set, migration 80) is the row's level, its filter and its place worst first, said as "On track, set by
// you"; set: { level, auto, who, at } then, else null. me: the staff member's own person, for "you".
export function memberRow(m, { me = null } = {}) {
  const s = m.summary || {}
  const open = s.open_month
  const months = s.term_months || 12
  const needs = needsAll(s)
  const level = levelFromCounts(s)
  const counts = countsFromSummary(s)
  const set = level.set && s.level_set ? { level: level.key, auto: level.auto, who: whoSet(s.level_set, me), at: s.level_set.set_at || null } : null
  // the last tick and the last log sent, once the summary has them (the build plan's migration 80)
  const lastTick = s.log?.last_tick_at ? dm(s.log.last_tick_at) : null
  // sessions as the log counts them once the summary has its counts (the whole log, as the level and the Actions column
  // count; the review of 22 Oct), else the summary's own, inside the term
  const se = sessionsCounted(s)
  return {
    id: m.person_id,
    name: m.full_name,
    first: m.first_name || (m.full_name || '').split(' ')[0],
    cohortId: m.cohort_id,
    group: m.cohort_name,
    status: m.access_status,
    chip: MM.access[m.access_status] || null,
    state: level.key,
    rank: RANK[level.key] ?? 3,
    month: s.term_month ? MM.monthOf(s.term_month, months) : '',
    monthN: s.term_month || null,
    thisMonth: open ? (open.total ? MM.ticked(open.ticked, open.total, lastTick) : MM.noActionsSet) : MM.noActionsSet,
    sessions: se.counted ? MM.sessionsOf(se.live || 0, se.counted) : MM.noneYet2,
    actions: counts ? (counts.actions.due ? MM.actionsDone(counts.actions.done, counts.actions.due) : MM.noneReviewed) : null,
    stand: set ? MM.levelSetCell(MM.level[level.key], set.who) : MM.levelLine(counts ? level : { ...level, notDone: 0 }),
    set,
    needs: needs[0] || null,
    needsAll: needs,
    also: needs.length > 1 ? MM.also(needs.slice(1).map(n => n.also)) : null,
    registerMissing: (s.flags?.register_missing || []).map(r => r.session_id),
    off: isBehind(level.key),
    money: MONEY.includes(m.access_status),
    review: needs.some(n => n.kind === 'review'),
    logKnown: Boolean(s.log),
    lastSent: s.log?.last_sent_at || null,
  }
}

// The sessions counted and attended live for a row and the person card: the log's own counts (summary.log, migration 80),
// which count every session the member's log lists, as the level and the Actions column do; before 80, the summary's,
// which count inside the term only. The 2 part once a member is past month 12 or has a register line from before their
// access began, so the row and the card must read the same counts as the level beside them.
export function sessionsCounted(s = {}) {
  const c = countsFromSummary(s)
  if (c) return { live: c.sessions.live, counted: c.sessions.counted }
  const se = s.sessions || {}
  return { live: se.live || 0, counted: se.counted || 0 }
}

// First name order, then surname
export const byName = (a, b) => (a.first || '').localeCompare(b.first || '', 'en-GB') || (a.name || '').localeCompare(b.name || '', 'en-GB')
// Worst first, then by name (the critique's admin 9)
export const byLevel = (a, b) => (a.rank ?? 3) - (b.rank ?? 3) || byName(a, b)

// The group tabs: each group with its count, "No group" when anyone has none, then "Everyone"
export function groupTabs(rows, groups) {
  const tabs = groups.map(g => ({ key: g.id, label: MM.groupTab(g.name, rows.filter(r => r.cohortId === g.id).length), name: g.name }))
  const none = rows.filter(r => !r.cohortId).length
  if (none) tabs.push({ key: 'none', label: MM.noGroup(none), name: null })
  tabs.push({ key: 'all', label: MM.everyone(rows.length), name: null })
  return tabs
}

// The rows for a tab, a filter and a search. A search looks in every group, whichever tab is open (the critique's admin
// 12). Each tab sorts worst first, then by name; the review (show=review, from "Review, starting with") and order 'name'
// keep name order, so Previous and Next never jump about.
export function pickRows(rows, { group = 'all', show = 'all', search = '', order } = {}) {
  const q = search.trim().toLowerCase()
  const sort = (order || (show === 'review' ? 'name' : 'level')) === 'name' ? byName : byLevel
  return rows
    .filter(r => q || group === 'all' || (group === 'none' ? !r.cohortId : r.cohortId === group))
    .filter(r => show === 'needs' ? Boolean(r.needs) : show === 'off' ? r.off : show === 'review' ? r.review : show === 'money' ? r.money : true)
    .filter(r => !q || (r.name || '').toLowerCase().includes(q))
    .sort(sort)
}

// The first time, the group whose session was most recent
export function defaultGroup(groups, sessions, nowMs) {
  const held = (sessions || []).filter(s => s.cohort_id && s.status !== 'cancelled' && s.starts_at && Date.parse(s.starts_at) <= nowMs)
    .sort((a, b) => Date.parse(b.starts_at) - Date.parse(a.starts_at))
  const g = held.find(s => groups.some(x => x.id === s.cohort_id))
  return g ? g.cohort_id : groups[0]?.id || 'all'
}

// After a session: the group's session (or 1 for every group) that ended in the last 7 days, as the group's checklist
// (the critique's admin 7): the register, the recording, the next session's Zoom link and the reviews, each done or
// not. { day, left, total, first, register, marked, session, recording, next, done }, or null.
// recordings: the product's recordings (null while unread, so the line isn't drawn); next: the product's sessions to
// come, with their Zoom links.
export function afterSession(group, sessions, rows, nowMs, { recordings = null, next = null } = {}) {
  if (!group || group === 'all' || group === 'none') return null
  const ended = (sessions || []).filter(s => s.status !== 'cancelled' && s.starts_at && (!s.cohort_id || s.cohort_id === group))
    .map(s => ({ ...s, ends: Date.parse(s.starts_at) + (s.duration_minutes || 0) * 60000 }))
    .filter(s => s.ends <= nowMs && s.ends > nowMs - 7 * DAY)
    .sort((a, b) => b.ends - a.ends)[0]
  if (!ended) return null
  const mine = rows.filter(r => r.cohortId === group).sort(byName)
  const due = mine.filter(r => r.review)
  const missing = mine.filter(r => (r.registerMissing || []).includes(ended.id)).length
  const coming = next
    ? next.filter(s => s.status !== 'cancelled' && s.starts_at && Date.parse(s.starts_at) > nowMs && (!s.cohort_id || s.cohort_id === group))
      .sort((a, b) => Date.parse(a.starts_at) - Date.parse(b.starts_at))[0] || null
    : undefined
  const recording = recordings ? recordings.some(r => r.session_id === ended.id) : null
  return {
    day: wdm(ended.starts_at), left: due.length, total: mine.length, first: due[0] || null,
    register: missing > 0, marked: mine.length - missing, session: ended.id,
    recording, next: coming,
    done: !due.length && !missing && recording !== false && (coming === undefined || Boolean(coming?.zoom_join_url)),
  }
}

// On Everyone, each group with reviews still due gets its own strip (the critique's admin 1): [{ group, name, after }]
export function afterSessions(groups, sessions, rows, nowMs, extra = {}) {
  return (groups || []).map(g => ({ group: g.id, name: g.name, after: afterSession(g.id, sessions, rows, nowMs, extra) }))
    .filter(x => x.after && x.after.left > 0)
}

// "8 to 12 Sep", "28 Oct to 3 Nov", or "since 3 Nov" while it lasts
export function shortSpan({ from, to }) {
  if (!to) return MM.span(dm(from), null)
  const a = uk(from)
  const b = uk(to)
  return MM.span(a.y === b.y && a.m === b.m ? String(a.d) : dm(from), dm(to))
}

// The goal as a dated fact: "Goal agreed 28 Jun", "Goal reached 29 Sep", "Goal not agreed yet". Whether it was agreed
// in time is the guarantee check's, not this.
export function goalFact(go = {}) {
  if (go.reached_on) return MM.strip.goal.reached(dm(go.reached_on))
  if (go.agreed_on && ['agreed', 'agreed_late', 'agreed_unjudged'].includes(go.state)) return MM.strip.goal.agreed(dm(go.agreed_on))
  return MM.strip.goal.none
}

// The membership as a dated fact: "Suspended 8 to 12 Sep", "Payment overdue", "Paused since 9 Oct", "Membership up to date"
export function membershipFact(me = {}) {
  const suspended = me.suspended || []
  if (suspended.length) return MM.strip.membership.suspended(suspended.map(shortSpan).join(' and '))
  if (me.overdue) return MM.strip.membership.overdue
  const pausedNow = (me.paused || []).find(p => !p.to)
  if (pausedNow) return MM.strip.membership.paused(dm(pausedNow.from))
  return MM.strip.membership.upToDate
}

// The member view's strip of 4: attended live and actions done, counted as the member's own log counts them
// (accountability in log.js), each marked as the level is (any slip marks it); then the goal and the membership as
// dated facts, with no mark. [{ key, text, mark }]
export function measureStrip(log) {
  const acc = accountability(log)
  const se = acc.sessions
  const ac = acc.actions
  const sum = log?.summary || {}
  return [
    { key: 'sessions', text: MM.strip.sessions(se.live, se.counted), mark: se.counted ? (se.live < se.counted ? 'off' : 'on') : 'todo' },
    { key: 'actions', text: MM.strip.actions(ac.done, ac.due), mark: ac.due ? (ac.notDone ? 'off' : 'on') : 'todo' },
    { key: 'goal', text: goalFact(sum.goal), mark: null },
    { key: 'membership', text: membershipFact(sum.membership), mark: null },
  ]
}

// The person page's card lines, from the summary: attended live, the actions (the log's own counts once the summary
// has them, none before then, as on the list), and the goal and the membership as dated facts. me: as memberRow's.
export function cardLines(m, product, { me = null } = {}) {
  const s = m.summary || {}
  const row = memberRow(m, { me })
  const se = sessionsCounted(s)
  const counts = countsFromSummary(s)
  return {
    line: MM.cardLine({ group: m.cohort_name, month: s.term_month, months: s.term_months || product?.term_months || 12, stand: row.stand }),
    measures: MM.cardMeasures({
      sessions: MM.strip.sessions(se.live || 0, se.counted || 0),
      actions: counts ? MM.strip.actions(counts.actions.done, counts.actions.due) : null,
      goal: goalFact(s.goal),
      membership: membershipFact(s.membership),
    }),
    needs: row.needs ? MM.cardNeeds(row.needs.text) : null,
  }
}

// The member view's head line: "Group 2. Membership month 3 of 12. Joined 25 Jul 2026."
export function headLine(log) {
  const a = log?.access || {}
  return MM.headLine({ group: a.cohort_name, month: a.term_month, months: log?.product?.term_months || 12, joined: a.start_day ? dmy(a.start_day) : null })
}

// Needs you, in the member view: every item that applies, each with where it's fixed. The months copied from the old
// site aren't a job, so they're noted in Earlier months instead (the critique's admin 6).
export function needsList(log, first) {
  const f = log?.summary?.flags || {}
  const out = []
  if (f.review_due) out.push({ key: 'review', text: MM.needReview(f.review_due.month, wdm(f.review_due.starts_at)), target: '#mm-this-month' })
  for (const r of f.register_missing || []) out.push({ key: `register-${r.session_id}`, text: MM.needRegister(wdm(r.starts_at)), target: '#mm-sessions' })
  if (f.goal_not_agreed) out.push({ key: 'goal', text: MM.needGoal(first), target: '#mm-goal' })
  if (f.no_actions) out.push({ key: 'actions', text: MM.needActions(f.no_actions.next_month), target: '#mm-this-month' })
  for (const c of f.carried_3 || []) out.push({ key: `carried-${c.action_id}`, text: MM.needCarried(c.body), target: '#mm-this-month' })
  return out
}

// The months that came from the old site, said once, in Earlier months
export function copiedNote(log) {
  const months = log?.summary?.flags?.copied_months || []
  return months.length ? MM.needCopied(months) : null
}

// The member view's head with a level set by hand (the staff log's level_set, migration 80): { line, note }, or null
// with none. "Set by you on 22 Oct: On track. Automatic: Well behind." and the note under it.
export function levelHead(log, me = null) {
  const set = log?.level_set
  if (!set || !MM.level[set.level]) return null
  const who = whoSet(set, me)
  const auto = accountability(log).auto
  return {
    line: MM.levelSetHead({ who, day: set.set_at ? dm(set.set_at) : '', level: MM.level[set.level], auto: MM.level[auto.key] || MM.level.getting_started }),
    note: set.note ? MM.levelNote(who, set.note) : null,
  }
}

// Before you send it: only what Adam can fix, and the copied months to note, and a level set by hand (the log he sends
// says the level he set)
export function beforeSend(log, first) {
  const f = log?.summary?.flags || {}
  const out = []
  if (log?.level_set && MM.level[log.level_set.level]) {
    out.push({ key: 'level', text: MM.levelBeforeSend(first, MM.level[log.level_set.level], MM.level[accountability(log).auto.key] || MM.level.getting_started), target: null })
  }
  const gaps = (f.register_missing || []).map(r => wdm(r.starts_at))
  if (gaps.length) out.push({ key: 'register', text: MM.registerGaps(gaps), target: 'sessions' })
  if (f.review_due) out.push({ key: 'review', text: MM.reviewDue(f.review_due.month, wdm(f.review_due.starts_at)), target: 'month' })
  if (log?.summary?.goal?.state && !['agreed', 'agreed_unjudged', 'agreed_late', 'reached'].includes(log.summary.goal.state)) out.push({ key: 'goal', text: MM.goalNot(first), target: 'goal' })
  if ((f.copied_months || []).length) out.push({ key: 'copied', text: MM.needCopied(f.copied_months), target: null })
  return out
}

// The months a closed month's carry can go to, and the next month's number, as private.mm_open_cycle
// picks it: the later of the month after the last and the term month now
export function nextMonthOf(log) {
  const last = Math.max(0, ...(log?.months || []).map(m => m.month || 0))
  return Math.min(120, Math.max(last + 1, log?.access?.term_month || 1))
}

// The sessions the open month can be set at (choices: words.js sessionChoices'), less any that starts at or before the
// start of the month before (its set-at session, else the day it was started), the rule hub.set_month_session holds
// to, so months never overlap. Every choice with no month before.
export function monthSessionChoices(log, choices = []) {
  const before = monthBeforeOpen(log)
  if (before === undefined) return []
  const from = before ? Date.parse(before.set_at || before.started_at || '') : NaN
  if (!Number.isFinite(from)) return choices
  return choices.filter(c => Date.parse(c.starts_at) > from)
}

// The month just before the open 1 as private.mm_months orders them (month, then started_at, then the month's id), as
// hub.set_month_session picks it (a merge can leave 2 months with the same number; the review of 22 Oct): null with
// none, undefined with no month open
function monthBeforeOpen(log) {
  const months = log?.months || []
  const open = months.find(m => !m.closed_at)
  if (!open) return undefined
  const at = m => Date.parse(m.started_at || 0) || 0
  const order = (x, y) => x.month - y.month || at(x) - at(y) || (x.cycle_id < y.cycle_id ? -1 : x.cycle_id > y.cycle_id ? 1 : 0)
  return months.filter(m => m.cycle_id !== open.cycle_id && order(m, open) < 0).sort(order).pop() || null
}

// What a change of the open month's session did to the guarantee check (migration 80: set_month_session's answer and
// its audit line keep its state and what's off, before and after, since the month before's span moves with it and a
// month wholly inside a pause doesn't count; the review of 22 Oct): the words, or null when nothing moved. Owner and
// admin only, as everything on Masterminds.
export function guaranteeMoved(g) {
  const b = g?.before
  const a = g?.after
  if (!b || !a) return null
  const word = st => (MM.stand[st] || st || '').toLowerCase()
  if (b.state !== a.state) return MM.guaranteeMoved(word(b.state), word(a.state))
  if (JSON.stringify(b.off || []) !== JSON.stringify(a.off || [])) return MM.guaranteeCountsMoved
  return null
}

// The month before the open 1, by its number, for session_too_early's words, or null
export function monthBefore(log) {
  return monthBeforeOpen(log)?.month ?? null
}

// The last tick on any of their actions, from the log: "30 Aug", or null when nothing's been ticked
export function lastTickOf(log) {
  const at = (log?.months || []).flatMap(m => (m.actions || []).map(a => a.done_at)).filter(Boolean).sort().pop()
  return at ? dm(at) : null
}

// ---- The guarantee (owner and admin only; the build plan's question 3, Adam, 20 Oct) --------------------------------

// The 4 measures of 1 member's guarantee check under a set of numbers, worked out from their summary as
// private.mm_log_summary works them out: 'on', 'off' or 'none' (nothing to judge)
function judge(s, r, nowMs) {
  const se = s.sessions || {}
  const ac = s.actions || {}
  const go = s.goal || {}
  const counted = (se.live || 0) + (se.caught_up || 0) + (se.missed || 0)
  const sessions = !counted ? 'none' : (se.missed || 0) > r.limit ? 'off' : 'on'
  const actions = !ac.finished ? 'none' : (ac.done || 0) * 100 >= r.pct * ac.finished ? 'on' : 'off'
  let goal = 'none'
  if (go.due_on && Number.isFinite(go.days)) {
    // the day they joined is the due day the summary gives, less its days
    const start = Date.parse(`${go.due_on}T12:00:00Z`) - go.days * DAY
    const due = start + r.days * DAY
    const judged = !r.keptSince || due >= Date.parse(`${r.keptSince}T12:00:00Z`)
    if (go.reached_on) goal = 'on'
    else if (go.agreed_on) goal = !judged ? 'none' : (go.days_after ?? 0) <= r.days ? 'on' : 'off'
    else if (nowMs <= due + DAY) goal = 'on'
    else goal = judged ? 'off' : 'none'
  }
  return { sessions, actions, goal }
}

// What new numbers do before they're saved (the critique's admin 10): the members whose guarantee check comes out
// differently under them, by name, and whether the days to catch up changed, which may change more than the summary
// can count. { names, catchUp }
export function rulesEffect(members, before, after, nowMs = Date.now()) {
  const names = []
  for (const m of members || []) {
    const s = m.summary
    if (!s) continue
    const a = judge(s, before, nowMs)
    const b = judge(s, after, nowMs)
    if (a.sessions !== b.sessions || a.actions !== b.actions || a.goal !== b.goal) names.push(m.full_name)
  }
  names.sort((x, y) => String(x).localeCompare(String(y), 'en-GB'))
  return { names, catchUp: before.catchUp !== after.catchUp }
}

// The guarantee check's verdict, from the summary's 4 measures (its off list): { meets, text, reasons: [{ key, text,
// target }] }, each reason pointing at its proof on the page
export function termVerdict(log) {
  const sum = log?.summary || {}
  const rules = log?.rules || {}
  const off = sum.off || []
  if (!off.length && sum.state === 'getting_started') return { meets: null, text: MM.termVerdictNone, reasons: [] }
  if (!off.length) return { meets: true, text: MM.termVerdictMeets, reasons: [] }
  const go = sum.goal || {}
  const days = go.days ?? rules.goal_days ?? 30
  const reasons = off.map(k => {
    if (k === 'sessions') return { key: k, text: MM.termWhy.sessions(sum.sessions?.missed || 0, sum.sessions?.limit ?? rules.absence_limit ?? 2), target: '#mm-sessions' }
    if (k === 'actions') return { key: k, text: MM.termWhy.actions(sum.actions?.pct ?? 0, sum.actions?.needed ?? rules.actions_pct ?? 80), target: '#mm-earlier' }
    if (k === 'goal') return { key: k, text: go.state === 'agreed_late' ? MM.termWhy.goal_late(days) : MM.termWhy.goal_not(days), target: '#mm-goal' }
    if (k === 'membership') return { key: k, text: MM.termWhy.membership((sum.membership?.suspended || []).map(shortSpan).join(' and ')), target: null }
    return null
  }).filter(Boolean)
  return { meets: false, text: MM.termVerdictShort(reasons.map(r => r.text)), reasons }
}

// The guarantee check opens by itself from the term's last 2 months
export const termOpens = log => {
  const months = log?.product?.term_months || 12
  const now = log?.access?.term_month || log?.summary?.term_month || 0
  return now >= months - 1
}

// ---- The membership (the critique's admin 5) ------------------------------------------------------------------------

// The status line under a member's name when their access isn't active, from their access row and summary: { kind,
// text } or null
export function statusLine(row, sum, nowMs = Date.now()) {
  if (!row) return null
  if (row.status === 'overdue') {
    return { kind: 'overdue', text: MM.statusOverdue(row.payment_failed_at ? day(row.payment_failed_at, nowMs) : null, row.suspend_on ? day(row.suspend_on, nowMs) : null) }
  }
  if (row.status === 'suspended') {
    const open = (sum?.membership?.suspended || []).find(s => !s.to)
    const since = open?.from || row.suspend_on || null
    return { kind: 'suspended', text: MM.statusSuspended(since ? day(since, nowMs) : null) }
  }
  if (row.status === 'paused') {
    return { kind: 'paused', text: MM.statusPaused(row.paused_by, row.paused_until ? day(row.paused_until, nowMs) : null) }
  }
  return null
}

// ---- History of changes (ChangesCard.jsx) ------------------------------------------------------------------------

const word = o => (o ? (MM.outcomes[o] || o).toLowerCase() : '')
const mark = m => (m ? (MM.markWords[m] || m).toLowerCase() : null)

// Whether a line came with the old site's copy: made outside anyone's login, on or before the day the record was kept
// from (the log's copied.since, a London day)
export function fromCopy(h, since) {
  if (h.actor_kind !== 'system' || !since || !h.at) return false
  return new Date(h.at).toLocaleDateString('en-CA', { timeZone: 'Europe/London' }) <= since
}

// 1 line of the history, in words
export function historyLine(h, { first, sessions = [], since = null } = {}) {
  const who = h.actor_name || (h.actor_kind === 'system' ? (fromCopy(h, since) ? MM.oldSite : MM.the) : first)
  const body = h.body || ''
  const b = h.before || {}
  const a = h.after || {}
  const e = MM.ev
  let line
  switch (h.kind) {
    case 'goal_drafted': line = e.goal_drafted(who); break
    case 'goal_changed': line = e.goal_changed(who); break
    case 'goal_locked': line = e.goal_locked(who, a.changed_on ? dm(a.changed_on) : null); break
    case 'goal_unlocked': line = e.goal_unlocked(who); break
    case 'goal_reached': line = e.goal_reached(who, a.reached_on ? dm(a.reached_on) : null); break
    case 'action_added': line = e.action_added(who, body); break
    case 'carried': line = e.carried(who, body); break
    case 'ticked': line = e.ticked(who, body); break
    case 'unticked': line = e.unticked(who, body); break
    case 'noted': line = e.noted(who, body); break
    case 'edited': line = e.edited(who, b.body || '', a.body || ''); break
    case 'outcome_set': line = e.outcome_set(who, body, word(a.outcome)); break
    case 'outcome_corrected': line = e.outcome_corrected(who, body, word(a.outcome), h.reason); break
    case 'verdict_changed': line = e.verdict_changed(who, body); break
    case 'set_aside': line = e.set_aside(who, body, h.reason); break
    case 'put_back': line = e.put_back(who, body); break
    case 'log_sent': line = e.log_sent(who); break
    case 'log_saved': line = e.log_saved(who); break
    case 'attendance_changed': {
      const s = sessions.find(x => x.session_id === (a.session_id || h.session_id))
      line = e.attendance_changed(s ? wdm(s.starts_at) : MM.sessionsH, mark(a.mark_before), mark(a.mark_after))
      break
    }
    case 'catch_up_recorded': {
      const s = sessions.find(x => x.session_id === (a.session_id || h.session_id))
      line = e.catch_up_recorded(who, s ? wdm(s.starts_at) : MM.sessionsH, a.caught_up_on ? dm(a.caught_up_on) : null)
      break
    }
    case 'month_closed': line = e.month_closed(who, a.month, a.done, a.part_done, a.not_done); break
    case 'accountability_log_shared': line = e.accountability_log_shared(who, a.how); break
    // the level set by hand and the open month's session (migration 80): the levels only, never the note; a change of
    // the note alone, the level kept, says so rather than "from On track to On track" (the review of 22 Oct)
    case 'accountability_level_set': {
      const after = MM.level[a.level] || a.level
      line = b.level && b.level === a.level && a.note_changed
        ? e.accountability_level_note(who, after)
        : e.accountability_level_set(who, b.level ? MM.level[b.level] || b.level : null, after)
      break
    }
    case 'accountability_level_cleared': line = e.accountability_level_cleared(who); break
    case 'month_session_changed': {
      const s = a.session_id ? sessions.find(x => x.session_id === a.session_id) : null
      const moved = guaranteeMoved(a.guarantee)
      line = e.month_session_changed(who, a.month, s ? wdm(s.starts_at) : null, a.session_id == null) + (moved ? ` ${moved}` : '')
      break
    }
    default: line = null
  }
  // an action's own full stop closes a line that ends with it in quotes, never doubled ("...rent.".)
  return line ? MM.historyLine(dm(h.at), line.replace(/([.?!])"\.$/, '$1"')) : null
}
